import React, { useState, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useFollowUpTrackingCount, useFollowUpTrackingData } from '../../../hooks/useWidgetData';
import { toast } from '@rickylandino/react-messages';
import { CheckCircle, ExternalLink, Trash2 } from 'lucide-react';
import { Table, Modal } from 'antd';
import Globals from '../../../config/globals';
import PatientExamPane from '../../Exams/PatientExamPane';
import { calculateAge } from '../../Helpers/PatientHelper';
import { getPatientExamResults } from '../../../common/services/ExamService';
import { deleteTrackingAction } from '../../../common/services/next-gen/PatientTrackingService';

/**
 * FollowUpTrackingWidget component
 * Shows all patient results requiring follow-up tracking with server-side pagination
 * Displays 15 columns with patient and tracking information
 * @param {Object} props
 * @param {boolean} [props.isCollapsed=false] - Whether the widget is collapsed
 * @param {boolean} [props.isExpanded=false] - Whether the widget is in fullscreen mode
 * @param {Function} [props.setIsLoading] - Callback to set loading state for parent
 * @param {Function} [props.setSharedState] - Callback to update shared state (including refetch)
 */
export default function FollowUpTrackingWidget({ isCollapsed = false, isExpanded = false, setIsLoading, setSharedState, queryEnabled = true }) {
// Get query client for cache invalidation
    const queryClient = useQueryClient();
    const userInfo = JSON.parse(window.sessionStorage.getItem("userInfo"));
    
// Pagination state
const [currentPage, setCurrentPage] = useState(1);
const [pageSize, setPageSize] = useState(50);
    
// Exam pane state
const [showAddExamPane, setShowAddExamPane] = useState(false);
const [selectedPatient, setSelectedPatient] = useState(null);
const [selectedPatientExam, setSelectedPatientExam] = useState(null);
const [examResults, setExamResults] = useState(null);
const [sharedFollowUpIDs, setSharedFollowUpIDs] = useState(null);
const [facility, setFacility] = useState(null);
    
// Deletion modal state
const [showDeletionModal, setShowDeletionModal] = useState(false);
const [recordToDelete, setRecordToDelete] = useState(null);

    // Determine when to load full data: either expanded in card OR in fullscreen mode
    const shouldLoadFullData = !isCollapsed || isExpanded;

    // Fetch count when collapsed AND not expanded
    const { data: countData, isLoading: isLoadingCount } = useFollowUpTrackingCount((isCollapsed && !isExpanded) && queryEnabled);

    // Fetch full data when expanded in card or fullscreen
    const { 
        data, 
        isLoading, 
        isFetching,
        isError, 
        error,
        refetch
    } = useFollowUpTrackingData(currentPage, pageSize, shouldLoadFullData);

    const results = data?.data || [];
    const totalCount = data?.totalCount || 0;
    const count = shouldLoadFullData ? totalCount : (countData?.count || 0);

    // Notify parent of loading state for fullscreen overlay
    useEffect(() => {
        if (setIsLoading && isExpanded) {
            setIsLoading(isLoading || isFetching);
        }
    }, [isLoading, isFetching, isExpanded, setIsLoading]);

    // Show error toast when full data fails to load
    useEffect(() => {
        if (shouldLoadFullData && isError) {
            console.error('Failed to load follow-up tracking:', error);
            toast.error(`Failed to load follow-up tracking: ${error?.message || 'Unknown error'}`);
        }
    }, [shouldLoadFullData, isError, error]);

    // Set refetch function in shared state when it becomes available
    useEffect(() => {
        if (setSharedState && refetch) {
            setSharedState(prev => ({ ...prev, refetch }));
        }
    }, [setSharedState, refetch]);

    /**
     * Format MRN with spaces every 10 characters
     */
    const formatMRN = useCallback((mrn) => {
        if (!mrn) return '';
        return mrn.replace(/(.{10})/g, '$1 ').trim();
    }, []);

    /**
     * Format date to short format (M/D/YYYY)
     */
    const formatDate = useCallback((dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            month: 'numeric', 
            day: 'numeric', 
            year: 'numeric' 
        });
    }, []);

    /**
     * Handle viewing a patient exam - opens PatientExamPane
     */
    const handleViewExam = useCallback(async (row) => {
        try {
            // Get user info from session storage
            const userInfo = JSON.parse(window.sessionStorage.getItem("userInfo"));
            
            if (!userInfo?.userId) {
                toast.error('User information not found. Please log in again.');
                return;
            }

            // Validate required fields
            if (!row.patientExamId) {
                toast.error('Missing exam information');
                console.error('Missing patientExamId:', { row });
                return;
            }

            // Check if we have facilityID from API (will need to be added)
            if (!row.facilityId) {
                toast.warning('Facility information missing from API. Please contact support.');
                console.error('Missing facilityId in API response. Row:', row);
                return;
            }

            // Prepare request payload
            const postdata = {
                uniqueID: row.patientExamId,
                facilityID: row.facilityId,
                name: row.examClass, // examClass may need to be added to API
                userID: userInfo.userId
            };

            // Fetch exam results
            const data = await getPatientExamResults(postdata);

            if (!data) {
                throw new Error('No data returned from server');
            }

            let patient = {
                dob: row.dob || data.patientDob
            }

            // Set state to show the exam pane
            setSelectedPatient(patient);
            setSelectedPatientExam(data.examInfo);
            setExamResults(data.resultInfo);
            setSharedFollowUpIDs(data.examIDs);
            setFacility({
                facilityID: row.facilityID,
                facilityName: row.facilityName
            });
            setShowAddExamPane(true);

        } catch (error) {
            console.error('Error loading exam:', error);
            toast.error(`Failed to load exam: ${error?.message || 'Unknown error'}`);
        }
    }, []);

    /**
     * Handle deleting a tracking action
     */
    const handleDeleteTracking = useCallback((row) => {
        // Check if we have the tracking action ID from API
        if (!row.mostRecentTrackingActionId) {
            toast.warning('Unable to delete: Tracking action ID missing from API response');
            console.error('Missing mostRecentTrackingActionId in API response. Row:', row);
            return;
        }

        setRecordToDelete(row);
        setShowDeletionModal(true);
    }, []);

    /**
     * Close deletion modal and reset state
     */
    const closeDeletionModal = useCallback(() => {
        setShowDeletionModal(false);
        setRecordToDelete(null);
    }, []);

    /**
     * Handle the actual deletion after confirmation
     */
    const handleConfirmDelete = useCallback(async () => {
        try {
            if (!recordToDelete?.mostRecentTrackingActionId) {
                toast.error('Missing tracking action ID');
                return;
            }

            // Call the delete API
            await deleteTrackingAction(recordToDelete.mostRecentTrackingActionId);

            // Close modal
            closeDeletionModal();
            
            // Invalidate queries to trigger automatic refetch
            queryClient.invalidateQueries({ queryKey: ['widgetData', 'follow-up-tracking'] });
            queryClient.invalidateQueries({ queryKey: ['widgetCount', 'follow-up-tracking'] });
            
            // Show success message
            toast.success('Tracking action deleted successfully');

        } catch (error) {
            console.error('Error deleting tracking action:', error);
            toast.error(`Failed to delete: ${error?.response?.data?.message || error?.message || 'Unknown error'}`);
        }
    }, [recordToDelete, queryClient, closeDeletionModal]);

    /**
     * Update exam info after changes
     */
    const updateExamInfo = useCallback(() => {
        toast.destroy('loading');
        toast.destroy("updating");

        setShowAddExamPane(false);

        // Invalidate queries to trigger automatic refetch
        queryClient.invalidateQueries({ queryKey: ['widgetData', 'follow-up-tracking', currentPage, pageSize] });
        queryClient.invalidateQueries({ queryKey: ['widgetCount', 'follow-up-tracking'] });

        toast.success('Widget data refreshed');
    }, [queryClient, currentPage, pageSize]);

    /**
     * Get new exam info and refresh widget
     */
    const getNewExamInfo = useCallback(() => {
        toast.destroy('loading');
        toast.destroy("updating");

        setShowAddExamPane(false);

        // Invalidate queries to trigger automatic refetch
        queryClient.invalidateQueries({ queryKey: ['widgetData', 'follow-up-tracking', currentPage, pageSize] });
        queryClient.invalidateQueries({ queryKey: ['widgetCount', 'follow-up-tracking'] });
        
        toast.success('Widget data refreshed');
    }, [queryClient, currentPage, pageSize]);



    // Table columns - simple configuration without useMemo
    const columns = [
        {
            title: 'Patient Name',
            dataIndex: 'patientName',
            key: 'patientName',
            width: 180,
        },
        {
            title: 'MRN',
            dataIndex: 'mrn',
            key: 'mrn',
            render: (mrn) => formatMRN(mrn),
            width: 150,
        },
        {
            title: 'DOB',
            dataIndex: 'dob',
            key: 'dob',
            render: (dob) => formatDate(dob),
            width: 110,
        },
        {
            title: 'Referring Doc',
            dataIndex: 'referringPhysician',
            key: 'referringPhysician',
            width: 160,
        },
        {
            title: 'Facility',
            dataIndex: 'facilityName',
            key: 'facilityName',
            width: 150,
        },
        {
            title: 'Exam Date',
            dataIndex: 'examDate',
            key: 'examDate',
            render: (examDate) => formatDate(examDate),
            width: 110,
        },
        {
            title: 'Exam Type',
            dataIndex: 'examType',
            key: 'examType',
            width: 130,
        },
        {
            title: 'Result Date',
            dataIndex: 'mostRecentResultDate',
            key: 'mostRecentResultDate',
            render: (resultDate) => formatDate(resultDate),
            width: 110,
        },
        {
            title: 'BIRADS',
            dataIndex: 'mostRecentBirads',
            key: 'mostRecentBirads',
            width: 100,
        },
        {
            title: 'Result Notes',
            dataIndex: 'resultNotes',
            key: 'resultNotes',
            width: 180,
        },
        {
            title: 'Contact Date',
            dataIndex: 'mostRecentContactDate',
            key: 'mostRecentContactDate',
            render: (contactDate) => formatDate(contactDate),
            width: 120,
        },
        {
            title: 'Contact Type',
            dataIndex: 'mostRecentContactType',
            key: 'mostRecentContactType',
            width: 140,
        },
        {
            title: 'Contacted By',
            dataIndex: 'mostRecentContactedBy',
            key: 'mostRecentContactedBy',
            width: 150,
        },
        {
            title: 'Contact Notes',
            dataIndex: 'mostRecentContactNotes',
            key: 'mostRecentContactNotes',
            width: 180,
        },
        {
            title: 'Next F/U Date',
            dataIndex: 'nextFollowUpDate',
            key: 'nextFollowUpDate',
            render: (followUpDate) => formatDate(followUpDate),
            width: 120,
        },
        {
            title: 'Actions',
            key: 'action',
            width: 140,
            fixed: 'right',
            render: (_, row) => (
                <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            handleViewExam(row);
                        }}
                        className="px-3 py-1.5 rounded-lg font-medium text-sm transition-colors inline-flex items-center gap-1"
                        style={{
                            backgroundColor: Globals.theme === 'dark' ? '#ec4899' : '#db2777',
                            color: 'white',
                            border: 'none',
                            cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 
                                Globals.theme === 'dark' ? '#db2777' : '#be185d';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 
                                Globals.theme === 'dark' ? '#ec4899' : '#db2777';
                        }}
                        title="View exam"
                    >
                        View
                        <ExternalLink size={14} />
                    </button>
                    {row.mostRecentContactedById === userInfo.userId &&
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteTracking(row);
                            }}
                            className="p-1.5 rounded-lg transition-colors"
                            style={{
                                backgroundColor: 'transparent',
                                color: Globals.theme === 'dark' ? '#f87171' : '#dc2626',
                                border: `1px solid ${Globals.theme === 'dark' ? '#f87171' : '#dc2626'}`,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor =
                                    Globals.theme === 'dark' ? '#7f1d1d' : '#fee2e2';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                            title="Delete tracking action"
                        >
                            <Trash2 size={16} />
                        </button>
                    }
                </div>
            )
        }
    ];


    /**
     * Get count color based on theme
     */
    const getCountColor = () => {
        return Globals.theme === 'dark' ? '#ec4899' : '#db2777';
    };

    /**
     * Get count message
     */
    const getCountMessage = () => {
        return count === 1 ? 'patient requires follow-up tracking' : 'patients require follow-up tracking';
    };

    // COLLAPSED STATE - Show only count
    if (isCollapsed && !isExpanded) {
        if (isLoadingCount) {
            return (
                <div className="flex items-center justify-center py-4">
                    <div 
                        className="animate-spin rounded-full h-8 w-8 border-b-2"
                        style={{ borderColor: Globals.theme === 'dark' ? '#a1a1aa' : '#71717a' }}
                    />
                </div>
            );
        }

        return (
            <div className="flex flex-col items-center justify-center py-4">
                <div 
                    className="text-5xl font-light mb-2"
                    style={{ color: getCountColor() }}
                >
                    {count.toLocaleString()}
                </div>
                <div 
                    className="text-sm text-center px-4"
                    style={{ color: Globals.theme === 'dark' ? '#a1a1aa' : '#71717a' }}
                >
                    {getCountMessage()}
                </div>
            </div>
        );
    }

    // Success - no patients requiring follow-up tracking
    if (!isLoading && count === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-8">
                <CheckCircle 
                    className="h-16 w-16 mx-auto mb-4"
                    style={{ color: getCountColor() }}
                />
                <p 
                    className="text-lg font-medium"
                    style={{ color: getCountColor() }}
                >
                    All Patients Up to Date!
                </p>
                <p 
                    className="text-sm mt-2"
                    style={{ color: Globals.theme === 'dark' ? '#a1a1aa' : '#71717a' }}
                >
                    No patients currently require follow-up tracking
                </p>
            </div>
        );
    }

    // EXPANDED STATE - Show table with server-side pagination
    return (
        <>
            <div className="space-y-4">
                <Table
                    columns={columns}
                    dataSource={results}
                    rowKey={(record) => record.patientResultId}
                    pagination={{
                        current: currentPage,
                        pageSize: pageSize,
                        total: totalCount,
                        onChange: (page, newPageSize) => {
                            setCurrentPage(page);
                            if (newPageSize !== pageSize) {
                                setPageSize(newPageSize);
                                setCurrentPage(1);
                            }
                        },
                        pageSizeOptions: ['25', '50', '100', '200'],
                        showSizeChanger: true,
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} patients`,
                    }}
                    size="small"
                    scroll={{ y: 400 }}
                    loading={isLoading || isFetching}
                />
            </div>

            {/* Patient Exam Pane */}
            {showAddExamPane && selectedPatient && (
                <PatientExamPane
                    patientAge={calculateAge(selectedPatient?.dob)}
                    patientInactive={false}
                    patient={selectedPatient}
                    showPane={showAddExamPane}
                    examInfo={selectedPatientExam}
                    examHistory={examResults}
                    facility={facility}
                    updateExamInfo={updateExamInfo}
                    from='follow-up-tracking'
                    hidePane={() => setShowAddExamPane(false)}
                    sharedFollowUpIDs={sharedFollowUpIDs}
                    getNewExamInfo={getNewExamInfo}
                />
            )}

            {/* Deletion Confirmation Modal */}
            <Modal
                title="Delete Tracking Action"
                visible={showDeletionModal}
                onCancel={closeDeletionModal}
                onOk={handleConfirmDelete}
                okText="Delete"
                cancelText="Cancel"
                okButtonProps={{
                    danger: true,
                    style: { 
                        backgroundColor: '#dc2626',
                        borderColor: '#dc2626'
                    }
                }}
            >
                <p>Are you sure you want to delete this tracking action?</p>
                {recordToDelete && (
                    <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#f9fafb', borderRadius: '6px' }}>
                        <p><strong>Patient:</strong> {recordToDelete.patientName}</p>
                        <p><strong>MRN:</strong> {recordToDelete.mrn}</p>
                        {recordToDelete.mostRecentContactDate && (
                            <p><strong>Contact Date:</strong> {formatDate(recordToDelete.mostRecentContactDate)}</p>
                        )}
                        {recordToDelete.mostRecentContactType && (
                            <p><strong>Contact Type:</strong> {recordToDelete.mostRecentContactType}</p>
                        )}
                    </div>
                )}
                <p style={{ marginTop: '16px', color: '#dc2626' }}>
                    <strong>Warning:</strong> This action cannot be undone.
                </p>
            </Modal>
        </>
    );
}
